import React from "react";
import BaseSelect, { BaseSelectOption } from "../../Base/BaseSelect";

/** Punto adicional en edición: `key` estable para React, `location` = id de Location como string. */
export type RouteStopDraft = { key: string; location: string };

/** Punto adicional tal como lo entrega la API (`stops`). */
export type RouteStop = {
  id: number;
  order: number;
  location: number;
  location_name: string | null;
};

export const MAX_ROUTE_STOPS = 10;

let stopKeySeq = 0;
export const newStopDraft = (location = ""): RouteStopDraft => ({
  key: `stop-${++stopKeySeq}`,
  location,
});

export const stopsToDrafts = (stops: RouteStop[] | undefined | null): RouteStopDraft[] =>
  (stops ?? []).map((s) => newStopDraft(String(s.location)));

type Props = {
  stops: RouteStopDraft[];
  onChange: (next: RouteStopDraft[]) => void;
  options: BaseSelectOption[];
  disabled?: boolean;
};

/**
 * Lista ordenada de puntos adicionales entre origen y destino.
 * Independiente de la secuencia de pasajeros.
 */
const RouteStopsEditor: React.FC<Props> = ({ stops, onChange, options, disabled }) => {
  const update = (index: number, location: string) =>
    onChange(stops.map((s, i) => (i === index ? { ...s, location } : s)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= stops.length) return;
    const next = [...stops];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (index: number) => onChange(stops.filter((_, i) => i !== index));

  return (
    <div className="sp-route-stops">
      {stops.map((stop, index) => (
        <div className="sp-route-stop-row" key={stop.key}>
          <div className="sp-route-stop-select">
            <BaseSelect
              label={`Punto ${index + 1}`}
              name={`route_stop_${index}`}
              value={stop.location}
              onChange={(e) => update(index, e.target.value)}
              placeholder="Seleccionar punto"
              preserveCase
              searchable
              options={options}
              disabled={disabled}
            />
          </div>
          <div className="sp-route-stop-actions">
            <button
              type="button"
              className="sp-route-stop-btn"
              onClick={() => move(index, -1)}
              disabled={disabled || index === 0}
              title="Subir punto"
              aria-label={`Subir punto ${index + 1}`}
            >
              ↑
            </button>
            <button
              type="button"
              className="sp-route-stop-btn"
              onClick={() => move(index, 1)}
              disabled={disabled || index === stops.length - 1}
              title="Bajar punto"
              aria-label={`Bajar punto ${index + 1}`}
            >
              ↓
            </button>
            <button
              type="button"
              className="sp-route-stop-btn sp-route-stop-btn--danger"
              onClick={() => remove(index)}
              disabled={disabled}
              title="Quitar punto"
              aria-label={`Quitar punto ${index + 1}`}
            >
              ×
            </button>
          </div>
        </div>
      ))}
      {stops.length < MAX_ROUTE_STOPS && (
        <button
          type="button"
          className="sp-route-stop-add"
          onClick={() => onChange([...stops, newStopDraft()])}
          disabled={disabled}
        >
          + Agregar punto
        </button>
      )}
    </div>
  );
};

export default RouteStopsEditor;
